import React, { useEffect, useState } from "react"

// libraries
import Skeleton from "react-loading-skeleton"

// helpers
import Api from "@/_helpers/Api"

// providers
import { trans } from "@/_providers/Translation"
import { Tooltip } from "@mui/material"

// Interpolates the gradient end-color based on the bar's fill percentage:
// 0%   → #FFCBB0 (flat, no intensity)
// 50%  → #FFA486 (medium intensity)
// 100% → #FF5B79 (high intensity)
function getBarGradient(percentage) {
  const p = Math.min(100, Math.max(0, percentage))
  let r, g, b
  if (p <= 50) {
    const t = p / 50
    r = 255
    g = Math.round(203 + (164 - 203) * t)
    b = Math.round(176 + (134 - 176) * t)
  } else {
    const t = (p - 50) / 50
    r = 255
    g = Math.round(164 + (91 - 164) * t)
    b = Math.round(134 + (121 - 134) * t)
  }
  const hex = (v) => v.toString(16).padStart(2, "0")
  return `linear-gradient(to right, #FFCBB0, #${hex(r)}${hex(g)}${hex(b)})`
}

function getCategoriesFromApi(apiChartData) {
  if (!apiChartData) return []
  const labels = Array.isArray(apiChartData.labels) ? apiChartData.labels : []
  const values = Array.isArray(apiChartData.values) ? apiChartData.values : []
  const percentages = Array.isArray(apiChartData.percentages)
    ? apiChartData.percentages
    : []
  return labels.map((label, idx) => ({
    label,
    value: values[idx] || 0,
    percentage: percentages[idx] || 0,
  }))
}

function InventoryLevel() {
  const [loading, setLoading] = useState(true)
  const [stocks, setStocks] = useState([])
  const [loadingStoreload, setLoadingStoreload] = useState(false)
  const [storages, setStorages] = useState([])
  const [filters, setFilters] = useState({ storage_id: "" })

  useEffect(() => {
    loadData()
  }, [filters])

  function loadData() {
    setLoading(true)

    Api.get(
      `analytics/storage-stock-levels?storage_location_id=${filters.storage_id}`,
    )
      .then((res) => {
        setStocks(res.data.data)
        setStorages(res.data.storages || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  function handleLoadStock(category) {
    Api.post(
      `analytics/load-part-demand?product_id=${category.product_id}&storage_location_id=${filters.storage_id || storages[0]?.storage.id}&quantity=${category.needed_quantity - category.quantity}`,
    )
      .then(() => {
        setLoadingStoreload(true)
        setStocks((prevStocks) =>
          prevStocks.map((stock) =>
            stock.product_id === category.product_id
              ? {
                  ...stock,
                  quantity: stock.needed_quantity,
                  needed_quantity: 0,
                }
              : stock,
          ),
        )
      })
      .catch((err) => {
        console.error("Error loading stock:", err)
      })
  }

  function handleFilterChange(e) {
    const { name, value } = e.target

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <div className="inventory-level-chart">
      <div className="head">
        <h3>{trans("analytics.inventoryLevel")}</h3>
        <div className="right">
          {loading ? (
            <Skeleton height={40} width={140} count={1} />
          ) : (
            <select
              value={filters.storage_id || ""}
              name="storage_id"
              onChange={handleFilterChange}
            >
              {storages.map((storage) => (
                <option key={storage.storage.id} value={storage.storage.id}>
                  {storage.storage.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : stocks.length === 0 ? (
        <div
          className="alert info"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <div className="cbc-list-wrapper">
          {stocks.map((cat, idx, arr) => (
            <div className="cbc-row" key={cat.product_id}>
              <div className="cbc-row-head">
                <span className="cbc-label">{cat.product_name}</span>
                <span className="cbc-value">{cat.quantity}</span>
              </div>
              <Tooltip
                arrow
                title={
                  <>
                    <div className="needed-load-tooltip">
                      <table>
                        <ul>
                          <li>
                            <span>{trans("analytics.availableNow")}:</span>
                            <b>{cat.quantity}</b>
                          </li>
                          <li>
                            <span>{trans("analytics.neededForPeriod")}:</span>
                            <b>{cat.needed_quantity}</b>
                          </li>
                        </ul>
                      </table>
                      {cat.needed_quantity > cat.quantity ? (
                        <button
                          className="button-small"
                          onClick={() => handleLoadStock(cat)}
                          disabled={loadingStoreload}
                        >
                          {trans("analytics.orderItems", { count: cat.needed_quantity - cat.quantity })}
                        </button>
                      ) : (
                        <></>
                      )}
                    </div>
                  </>
                }
              >
                <div className="cbc-bar-bg">
                  {(() => {
                    const pct =
                      (cat.quantity /
                        Math.max(...arr.map((c) => c.quantity), 1)) *
                      100
                    return (
                      <div
                        className="cbc-bar"
                        style={{
                          background: getBarGradient(pct),
                          width: `${pct}%`,
                        }}
                      />
                    )
                  })()}
                </div>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default InventoryLevel
